import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {StateService} from '@uirouter/core';
import {UIRouterGlobals} from '@uirouter/angular';
import {filter, Subscription, take} from "rxjs";

import {DatabaseService} from "./database.service";
import {MenuService} from "./menu.service";
import {ParametersService} from "./parameters.service";
import {PopupService} from "./popup.service";
import {StylesService} from "./style.service";
import {SubtitlesService} from "./subtitles.service";
import {UpdateService} from "./update.service";
import {UserService} from "./user.service";
import {ViewsService} from "./views.service";
import {MenuType, ModuleData, ModuleStatus} from "../models";
import {SettingsModuleInfoService} from "./settings";

@Injectable({
    providedIn: 'root'
})
export class AccountModulesService {
    private database = inject(DatabaseService);
    private http = inject(HttpClient);
    private menu = inject(MenuService);
    private parameters = inject(ParametersService);
    private popup = inject(PopupService);
    private settingsModuleInfo = inject(SettingsModuleInfoService);
    private $state = inject(StateService);
    private styles = inject(StylesService);
    private subtitles = inject(SubtitlesService);
    private update = inject(UpdateService);
    private uiRouterGlobals = inject(UIRouterGlobals);
    private user = inject(UserService);
    private views = inject(ViewsService);

    downloadModules(): void {
        if (this.parameters.modules.length === 0) {
            this.getModules();
        }
    }

    getModules(): Subscription {
        this.views.hideView();
        this.update.stopInterval();

        let path = `/api/v1/users/${this.user.user_id}/modules`;

        return this.http.get<ModuleData[]>(path).subscribe({
            next: (response) => {
                this.parameters.modules = response;
                this.settingsModuleInfo.setFirstModuleAsDefaultIfNeeded(response);

                const modules = response;
                const selectedModuleHash = window.localStorage.getItem('selectedModuleHash');

                let index = selectedModuleHash
                    ? modules.findIndex((controller: ModuleData) => controller.udid === selectedModuleHash)
                    : modules.findIndex((controller: ModuleData) => controller.default);

                if (index === -1 && Array.isArray(modules) && modules.length) {
                    index = 0;
                }

                if (index !== -1) {
                    this.parameters.selectedModuleIndex = index;
                    this.parameters.selectedModuleType = modules[index].type;
                    this.parameters.selectedModuleCompany = modules[index].company;
                    this.parameters.selectedModule.moduleStatus = modules[index].moduleStatus === 'active';
                    this.parameters.selectedModule.controllerStatus = modules[index].controllerStatus === 'active';

                    this.parameters.selectedModuleHash = modules[index]['udid'];
                    window.localStorage.setItem('selectedModuleHash', this.parameters.selectedModuleHash);

                    this.styles.setStyle(modules[index].style);
                    this.$state.go(this.uiRouterGlobals.current.name, {selectedModuleHash: this.parameters.selectedModuleHash});
                } else {
                    this.parameters.clearModulesData();
                }

                this.views.showView();
                this.database.getModuleData().subscribe();
                this.menu.getMainMenu();

                let current_state = this.uiRouterGlobals.current.name.split('.')[0];
                if (!['alarm_history', 'statistics', 'settings'].includes(current_state)) {
                    this.update.startInterval();
                }

                this.popup.startPopupInterval();
            },
            error: () => {
                this.parameters.clearModulesData();
            }
        });
    }

    selectModule(index: number, style: string): void {
        if (index === this.parameters.selectedModuleIndex) return;

        this.views.hideView();
        this.styles.setStyle(style);
        this.update.stopInterval();
        this.menu.downloaded = false;
        this.update.lastUpdate = null;

        this.subtitles.getSubtitles('default', this.parameters.modules[index].tag);

        this.parameters.selectedModuleIndex = index;
        this.parameters.selectedModuleType = this.parameters.modules[index].type;
        this.parameters.selectedModuleCompany = this.parameters.modules[index].company;
        this.parameters.selectedModule.moduleStatus = this.parameters.modules[index].moduleStatus === 'active';
        this.parameters.selectedModule.controllerStatus = this.parameters.modules[index].controllerStatus === 'active';
        this.parameters.selectedModuleHash = this.parameters.modules[index].udid;

        window.localStorage.setItem('selectedModuleHash', this.parameters.modules[index].udid);

        this.views.showView();
        this.database.getModuleData().subscribe();
        this.menu.getMainMenu();
        this.update.startInterval();
        this.$state.go('home', {selectedModuleHash: this.parameters.selectedModuleHash});
    };

    getMenuItems(menuType: MenuType): void {
        const selectedModuleStatus: ModuleStatus = {
            moduleStatus: true,
            controllerStatus: true,
        }

        if (this.menu.downloaded) {
            this.showMenuItems(menuType, selectedModuleStatus);
            return;
        }

        this.menu.updatedMenu.pipe(
            filter(updated => updated && this.menu.downloaded),
            take(1),
        ).subscribe(() => this.showMenuItems(menuType, selectedModuleStatus));
    }

    private showMenuItems(menuType: MenuType, selectedModuleStatus: ModuleStatus): void {
        const menuItems = this.menu.mainMenu[menuType];
        if (!menuItems) return;

        this.menu.showSubmenu(menuItems, selectedModuleStatus);
        this.update.stopInterval();
        this.update.startInterval();
    }
}
